import { AgentApiRequestHelper } from './agent-api-request-helper';
import {
  AGENT_PLATFORM_API_BASE_URL,
  COMMENDA_CHAT_PROFILE_KEY,
} from './constants';
import type {
  AgentAttachmentUpload,
  AgentProfileModelListEnvelope,
  CommendaAiGeneratedFileRef,
  CommendaAiGeneratedFileSaveOptions,
  CommendaAiMessageFileRef,
  CommendaAiMutationResult,
  CommendaAiReferencePreview,
  CommendaAiReferenceType,
  CommendaAiSavedFile,
  SaveCommendaAiGeneratedFileBody,
} from './types';

/** A presigned S3 slot for a chat attachment. The key is derived from the caller's own id, so
 * neither field here can address another user's namespace. */
export async function createAgentAttachmentUpload(
  fileName: string,
  mimeType: string
): Promise<CommendaAiMutationResult<AgentAttachmentUpload>> {
  return AgentApiRequestHelper.mutate<AgentAttachmentUpload>(
    'agent/attachments/presigned-url',
    { method: 'POST', body: { fileName, mimeType } },
    'Could not prepare the upload.'
  );
}

/** A 302 to a signed download, so it is used as a link target rather than fetched. The message ref
 * lets a reader of a shared conversation download a file the agent generated there. */
export const agentAttachmentDownloadUrl = (
  fileUrl: string,
  messageRef?: CommendaAiMessageFileRef
) => {
  const params = new URLSearchParams({ fileUrl });
  if (messageRef) {
    params.set('conversationId', messageRef.conversationId);
    params.set('messageId', messageRef.messageId);
  }
  return `${AGENT_PLATFORM_API_BASE_URL}/attachments/download?${params}`;
};

/** A fresh inline url for an attachment, re-signed on every call so a preview never expires in place. */
export async function resolveAgentAttachmentViewUrl(
  fileUrl: string
): Promise<string> {
  const { url } = await AgentApiRequestHelper.read<{ url: string }>(
    `agent/attachments/view?fileUrl=${encodeURIComponent(fileUrl)}`
  );
  return url;
}

export async function fetchAgentReferencePreview(
  type: CommendaAiReferenceType,
  referenceId: string
): Promise<CommendaAiReferencePreview> {
  const params = new URLSearchParams({ type, referenceId });
  return AgentApiRequestHelper.read<CommendaAiReferencePreview>(
    `agent/references/preview?${params}`
  );
}

/** The models a reader may pick for one agent: registered, priced and reachable under the configured
 * routing, which is narrower than the whole catalogue. Per profile, because each agent allows its own. */
export async function listAgentProfileModels(
  profileKey: string = COMMENDA_CHAT_PROFILE_KEY
): Promise<AgentProfileModelListEnvelope> {
  return AgentApiRequestHelper.read<AgentProfileModelListEnvelope>(
    `agent/profiles/${profileKey}/models`
  );
}

/** Where a generated file would land in an entity's Documents, and what it would replace there. */
export async function fetchAgentGeneratedFileSaveOptions(
  ref: CommendaAiGeneratedFileRef,
  entityId: number,
  documentSubType: string | undefined
): Promise<CommendaAiGeneratedFileSaveOptions> {
  const params = new URLSearchParams({
    conversationId: ref.conversationId,
    messageId: ref.messageId,
    fileUrl: ref.fileUrl,
    entityId: entityId.toString(),
  });
  if (documentSubType) params.set('documentSubType', documentSubType);
  return AgentApiRequestHelper.read<CommendaAiGeneratedFileSaveOptions>(
    `agent/generated-files/save-options?${params}`
  );
}

export async function saveAgentGeneratedFile(
  body: SaveCommendaAiGeneratedFileBody
): Promise<CommendaAiMutationResult<CommendaAiSavedFile>> {
  return AgentApiRequestHelper.mutate<CommendaAiSavedFile>(
    'agent/generated-files/save',
    { method: 'POST', body },
    'Could not save the file to Documents.'
  );
}
